import { ILaya } from "../../ILaya";
import { NodeFlags } from "../Const";
import { Matrix } from "../maths/Matrix";
import { Point } from "../maths/Point";
import { Rectangle } from "../maths/Rectangle";
import { HTMLCanvas } from "../resource/HTMLCanvas";
import { Graphics } from "./Graphics";
import { Node } from "./Node";
import { RepaintFlag, SpriteConst, SubPassFlag, TransformKind } from "./SpriteConst";
import { RenderTexture2D } from "../resource/RenderTexture2D";
import { Event } from "../events/Event";
import { DragSupport } from "../utils/DragSupport";
import { URL } from "../net/URL";
import { LayaEnv } from "../../LayaEnv";
import { SpriteUtils } from "../utils/SpriteUtils";
import { RenderTargetFormat } from "../RenderEngine/RenderEnum/RenderTargetFormat";
import { BaseRenderNode2D } from "../NodeRender2D/BaseRenderNode2D";
import { SpriteGlobalTransform } from "./SpriteGlobaTransform";
import { LayaGL } from "../layagl/LayaGL";
import { BlendMode, BlendModeHandler } from "../webgl/canvas/BlendMode";
import { Stat } from "../utils/Stat";
import { GraphicsRenderer, SubStructRender } from "./Scene2DSpecial/GraphicsUtils";
import { PostProcess2D } from "./PostProcess2D";
import { Render2DProcessor } from "./Render2DProcessor";
import { ShaderFeatureType } from "../RenderEngine/RenderShader/Shader3D";
import { Config } from "../../Config";
import { MathUtil } from "../maths/MathUtil";
import { RenderCapable } from "../RenderEngine/RenderEnum/RenderCapable";
import { StatElement } from "../layagl/StatisticsContext";
import { ShaderDefines2D } from "../webgl/shader/d2/ShaderDefines2D";
const hiddenBits = NodeFlags.NOT_IN_PAGE;
export class Sprite extends Node {
    constructor() {
        super();
        this._x = 0;
        this._y = 0;
        this._width = 0;
        this._height = 0;
        this._scaleX = 1;
        this._scaleY = 1;
        this._skewX = 0;
        this._skewY = 0;
        this._pivotX = 0;
        this._pivotY = 0;
        this._anchorX = 0;
        this._anchorY = 0;
        this._rotation = 0;
        this._alpha = 1;
        this._blendMode = BlendMode.invalid;
        this._visible = true;
        this._mouseState = 0;
        this._zOrder = 0;
        this._renderType = 0;
        this._subpassUpdateFlag = 0;
        this.mouseThrough = false;
        this.hitTestPrior = false;
        this._autosize = false;
        this._repaint = -1;
        this._repaintCount = -1;
        this._previousType = 0;
        this._sizeFlag = 0;
        this._cacheAsBmp = false;
        this._layer = 0;
        this._ownPostProcess = false;
        this._manualRender = false;
        this._struct = LayaGL.render2DRenderPassFactory.createRenderStruct2D();
        this._struct.owner = this;
        this._globalTrans = new SpriteGlobalTransform(this);
    }
    _onActive() {
        super._onActive();
        LayaGL.statAgent.recordCountData(StatElement.C_Sprite2DCount, 1);
    }
    _onInActive() {
        super._onInActive();
        LayaGL.statAgent.recordCountData(StatElement.C_Sprite2DCount, -1);
    }
    _initShaderData() {
        if (this._shaderData)
            return;
        this._shaderData = LayaGL.renderDeviceFactory.createShaderData();
        BlendModeHandler.initBlendMode(this._shaderData);
        BlendModeHandler.setShaderData(this._struct.blendMode, this._shaderData);
        this._struct.spriteShaderData = this._shaderData;
        this._struct.isRenderStruct = true;
    }
    destroy(destroyChild = true) {
        super.destroy(destroyChild);
        if (this._texture) {
            this._texture._removeReference();
            this._texture = null;
        }
        if (this._oriRenderPass) {
            ILaya.stage.passManager.removePass(this._oriRenderPass);
            if (this._oriRenderPass.postProcess) {
                this._oriRenderPass.postProcess.destroy();
                this._oriRenderPass.postProcess = null;
            }
            this._oriRenderPass.destroy();
            this._oriRenderPass = null;
        }
        if (this._subStructRender) {
            this._subStructRender.destroy();
            this._subStructRender = null;
        }
        if (this._graphicsRenderer) {
            this._graphicsRenderer.destroy();
            this._graphicsRenderer = null;
        }
        if (this._drawOriRT) {
            if (this._drawOriRT !== RenderTexture2D._empty) {
                RenderTexture2D.recoverToPool(this._drawOriRT);
            }
            this._drawOriRT = null;
        }
        this.setGraphics(null);
        if (this._struct) {
            this._struct.destroy();
            this._struct = null;
        }
        if (this._globalTrans) {
            this._globalTrans.destroy();
            this._globalTrans = null;
        }
    }
    get parent() {
        return this._$parent;
    }
    get scene() {
        return this._scene;
    }
    get x() {
        return this._x;
    }
    set x(value) {
        this.pos(value, this._y);
    }
    get y() {
        return this._y;
    }
    set y(value) {
        this.pos(this._x, value);
    }
    get width() {
        if (this._autosize)
            return this.getSelfBounds(tmpRect).width;
        else if ((this._sizeFlag & 1) == 0)
            return this.measureWidth();
        else
            return this._width;
    }
    set width(value) {
        this.size(value, (this._sizeFlag & 2) == 0 ? null : this._height);
    }
    get height() {
        if (this._autosize)
            return this.getSelfBounds(tmpRect).height;
        else if ((this._sizeFlag & 2) == 0)
            return this.measureHeight();
        else
            return this._height;
    }
    set height(value) {
        this.size((this._sizeFlag & 1) == 0 ? null : this._width, value);
    }
    get _isWidthSet() {
        return (this._sizeFlag & 1) != 0;
    }
    get _isHeightSet() {
        return (this._sizeFlag & 2) != 0;
    }
    measureWidth() {
        return this._texture ? this._texture.width : 0;
    }
    measureHeight() {
        return this._texture ? this._texture.height : 0;
    }
    _isMaterialVaild(value) {
        return value.checkType(ShaderFeatureType.D2_TextureSV);
    }
    get displayWidth() {
        return this.width * this.scaleX;
    }
    get displayHeight() {
        return this.height * this.scaleY;
    }
    get scaleX() {
        return this._scaleX;
    }
    set scaleX(value) {
        this.scale(value, this._scaleY);
    }
    get scaleY() {
        return this._scaleY;
    }
    set scaleY(value) {
        this.scale(this._scaleX, value);
    }
    get rotation() {
        return this._rotation;
    }
    set rotation(value) {
        if (this._rotation !== value) {
            this._rotation = value;
            this._transChanged(TransformKind.Rotation);
        }
    }
    get skewX() {
        return this._skewX;
    }
    set skewX(value) {
        this.skew(value, this._skewY);
    }
    get skewY() {
        return this._skewY;
    }
    set skewY(value) {
        this.skew(this._skewX, value);
    }
    get transform() {
        if (!this._tfChanged)
            return this._transform;
        this._tfChanged = false;
        let m = this._transform || (this._transform = new Matrix());
        let sx = this._scaleX, sy = this._scaleY;
        let sskx = this._skewX;
        let ssky = this._skewY;
        let rot = this._rotation;
        if (rot || sx !== 1 || sy !== 1 || sskx !== 0 || ssky !== 0) {
            m._bTransform = true;
            let skx = (rot - sskx) * 0.0174532922222222;
            let sky = (rot + ssky) * 0.0174532922222222;
            let cx = Math.cos(sky);
            let ssx = Math.sin(sky);
            let cy = Math.sin(skx);
            let ssy = Math.cos(skx);
            m.a = sx * cx;
            m.b = sx * ssx;
            m.c = -sy * cy;
            m.d = sy * ssy;
            m.tx = m.ty = 0;
        }
        else {
            m.identity();
        }
        return m;
    }
    set transform(value) {
        this._tfChanged = false;
        let m = this._transform || (this._transform = new Matrix());
        if (value !== m)
            value.copyTo(m);
        if (value) {
            let out = Matrix.extractTransformInfo(value);
            this._x = out.x;
            this._y = out.y;
            this._scaleX = out.scaleX;
            this._scaleY = out.scaleY;
            this._skewX = out.skewX;
            this._skewY = out.skewY;
            this._rotation = out.rotation;
            m.tx = m.ty = 0;
            this._transChanged(TransformKind.TRS);
        }
        this.parentRepaint();
    }
    get globalTrans() {
        return this._globalTrans;
    }
    get pivotX() {
        return this._pivotX;
    }
    set pivotX(value) {
        this.pivot(value, this._pivotY);
    }
    get pivotY() {
        return this._pivotY;
    }
    set pivotY(value) {
        this.pivot(this._pivotX, value);
    }
    get anchorX() {
        return this._anchorX;
    }
    set anchorX(value) {
        this.anchor(value, this._anchorY);
    }
    get anchorY() {
        return this._anchorY;
    }
    set anchorY(value) {
        this.anchor(this._anchorX, value);
    }
    get alpha() {
        return this._alpha;
    }
    set alpha(value) {
        value = value < 0 ? 0 : (value > 1 ? 1 : value);
        if (this._alpha !== value) {
            this._alpha = value;
            this._struct.alpha = value;
            this.repaint();
        }
    }
    get visible() {
        return this._visible;
    }
    set visible(value) {
        if (this._visible !== value) {
            this._visible = value;
            this._processVisible();
        }
    }
    get blendMode() {
        return this._blendMode === 0 ? null : BlendMode[this._blendMode];
    }
    set blendMode(value) {
        let t = BlendMode[value];
        if (typeof (t) !== "number")
            t = value === "destination-out" ? BlendMode.destinationOut : 0;
        if (this._blendMode != t) {
            this._blendMode = t;
            this._initShaderData();
            this._struct.blendMode = this._blendMode;
            this.repaint(RepaintFlag.Graphics);
        }
    }
    get layer() {
        return this._layer;
    }
    set layer(value) {
        if (this._layer !== value) {
            if (value >= 0 && value <= 30) {
                this._layer = value;
                this._struct.renderLayer = 1 << value;
            }
            else {
                throw new Error("Layer value must be 0-30.");
            }
        }
    }
    get graphics() {
        if (!this._graphics) {
            this.graphics = new Graphics();
            this._ownGraphics = true;
        }
        return this._graphics;
    }
    set graphics(value) {
        this.setGraphics(value, false);
    }
    setGraphics(value, transferOwnership) {
        let g = this._graphics;
        this._graphics = null;
        if (g) {
            if (this._ownGraphics)
                g.destroy();
            else {
                g.owner = null;
            }
        }
        this._ownGraphics = transferOwnership;
        this._graphics = value;
        if (value) {
            if (!this._graphicsRenderer)
                this._graphicsRenderer = new GraphicsRenderer(this);
            value.owner = this;
            this._graphicsRenderer.setGraphics(value);
        }
        else {
            if (this._graphicsRenderer) {
                this._graphicsRenderer.destroy();
                this._graphicsRenderer = null;
            }
            this._renderType &= ~SpriteConst.GRAPHICS;
        }
        this.repaint(RepaintFlag.Graphics);
    }
    get filters() {
        return this._filterArr;
    }
    set filters(value) {
        if (value === this._filterArr)
            return;
        value && value.length === 0 && (value = null);
        this._filterArr = value;
        if (value) {
            let postProcess = this.getPostProcess(true);
            postProcess.clear();
            for (let f of this._filterArr) {
                postProcess.addEffect(f.getEffect());
            }
        }
        else
            this.postProcess = null;
        this.repaint();
    }
    getPostProcess(create = true) {
        if (!this._oriRenderPass || !this._oriRenderPass.postProcess) {
            if (create) {
                this.postProcess = new PostProcess2D();
                this._ownPostProcess = true;
            }
            else {
                return null;
            }
        }
        return this._oriRenderPass.postProcess;
    }
    get postProcess() {
        return this.getPostProcess(false);
    }
    set postProcess(value) {
        var _a;
        if ((_a = this._oriRenderPass) === null || _a === void 0 ? void 0 : _a.postProcess) {
            if (this._oriRenderPass.postProcess === value)
                return;
            if (this._ownPostProcess) {
                this._oriRenderPass.postProcess.destroy();
                this._ownPostProcess = false;
            }
            else {
                this._oriRenderPass.postProcess.owner = null;
            }
            this._oriRenderPass.postProcess = null;
            this.setSubpassFlag(SubPassFlag.PostProcess);
        }
        if (value) {
            if (this._manualRender)
                this._setManualRender(false);
            if (!this._oriRenderPass) {
                this.createSubRenderPass();
            }
            value.owner = this;
            this._oriRenderPass.postProcess = value;
            this.setSubpassFlag(SubPassFlag.PostProcess);
        }
    }
    get cacheAs() {
        return this._cacheAsBmp ? 'bitmap' : 'none';
    }
    set cacheAs(value) {
        let b = value === "bitmap";
        if (b === this._cacheAsBmp)
            return;
        if (b && this._manualRender)
            this._setManualRender(false);
        this._cacheAsBmp = b;
        if (b) {
            this._renderType |= SpriteConst.CANVAS;
        }
        else {
            this._renderType &= ~SpriteConst.CANVAS;
        }
        this.setSubpassFlag(SubPassFlag.CacheAsBitmap);
        this.repaint(RepaintFlag.Graphics);
    }
    get mask() {
        return this._mask;
    }
    set mask(value) {
        if (value == this || (value && this._mask == value && value._maskParent == this))
            return;
        if (value && value.isAncestorOf(this))
            throw new Error("Mask cannot be ancestor of the masked object");
        if (value && this._manualRender)
            this._setManualRender(false);
        if (this._mask) {
            this._mask.cacheAs = "none";
            this._mask._maskParent = null;
        }
        this._mask = value;
        if (value) {
            value._maskParent = this;
            value.cacheAs = "bitmap";
            this._renderType |= SpriteConst.MASK;
        }
        else {
            this._renderType &= ~SpriteConst.MASK;
        }
        this.setSubpassFlag(SubPassFlag.Mask);
        this.repaint();
    }
    _setManualRender(value) {
        if (value === this._manualRender)
            return;
        this._manualRender = value;
        value ? this._enableManualRender() : this._disableManualRender();
    }
    _enableManualRender() {
        if (this._renderType & SpriteConst.DRAW2RT) {
            if (this._cacheAsBmp)
                this.cacheAs = "none";
            if (this._mask)
                this.mask = null;
        }
        this._ensureSubPassBase();
        this._subStruct.manualRender = true;
        this._struct.subStruct = this._subStruct;
        this._subStruct.enabled = true;
        this._oriRenderPass.enable = false;
    }
    _disableManualRender() {
        if (!this._subStruct)
            return;
        this._subStruct.manualRender = false;
        this._subStruct.enabled = false;
        this._struct.subStruct = null;
        this._struct.setRepaint();
    }
    clearSubpassFlag(flag) {
        this._subpassUpdateFlag &= ~flag;
    }
    setSubpassFlag(flag) {
        this._subpassUpdateFlag |= flag;
        if (this._needUpdateSubpass()) {
            this.stage._subpassUpdateList.add(this);
            this._globalTrans._notifyRenderSpriteTransChange();
        }
    }
    _needUpdateSubpass() {
        let sprite = this._maskParent || this;
        return sprite.displayedInStage && sprite._struct.enabled;
    }
    get scrollRect() {
        return this._scrollRect;
    }
    set scrollRect(value) {
        if (this._scrollRect == null && value == null)
            return;
        this._scrollRect = value;
        if (value)
            this._renderType |= SpriteConst.CLIP;
        else
            this._renderType &= ~SpriteConst.CLIP;
        this._struct.setClipRect(value);
        if (this._oriRenderPass)
            this._oriRenderPass.repaint = true;
        this._globalTrans._spTransChanged(TransformKind.Layout);
        this.repaint();
    }
    get viewport() {
        return null;
    }
    set viewport(value) {
    }
    set drawCallOptimize(value) {
        if (this._struct.dcOptimize === value)
            return;
        this._struct.dcOptimize = value;
        this._struct.setRepaint();
        this.parentRepaint();
        value && this._globalTrans._spTransChanged(TransformKind.Layout);
    }
    get drawCallOptimize() {
        return this._struct.dcOptimize;
    }
    set enableCulling(value) {
        if (this._struct.enableCulling === value)
            return;
        this._struct.enableCulling = value;
        this._struct.setRepaint();
        this.parentRepaint();
        value && this._globalTrans._spTransChanged(TransformKind.Layout);
    }
    get enableCulling() {
        return this._struct.enableCulling;
    }
    get hitArea() {
        return this._hitArea;
    }
    set hitArea(value) {
        this._hitArea = value;
    }
    get mouseEnabled() {
        return this._mouseState === 2;
    }
    set mouseEnabled(value) {
        let i = value ? 2 : 1;
        if (this._mouseState !== i) {
            this._mouseState = i;
            if (i === 2)
                this.setMouseEnabledUp();
        }
    }
    setMouseEnabledUp() {
        let p = this._parent;
        while (p && p !== ILaya.stage) {
            if (p._mouseState !== 0 || !p._setBit(NodeFlags.CHECK_INPUT, true))
                break;
            p = p._parent;
        }
    }
    getMousePoint() {
        return this.globalToLocal(tmpPoint.setTo(ILaya.stage.mouseX, ILaya.stage.mouseY));
    }
    get mouseX() {
        return this.getMousePoint().x;
    }
    get mouseY() {
        return this.getMousePoint().y;
    }
    get globalScaleX() {
        return this.globalTrans.scaleX;
    }
    get globalScaleY() {
        return this.globalTrans.scaleY;
    }
    get zOrder() {
        return this._zOrder;
    }
    set zOrder(value) {
        if (this._zOrder != value) {
            this._zOrder = value;
            if (this._parent) {
                value && this._parent._setBit(NodeFlags.HAS_ZORDER, true);
                ILaya.systemTimer.callLater(this._parent, this.updateZOrder);
            }
        }
    }
    get zIndex() {
        return this._struct.zIndex;
    }
    set zIndex(value) {
        if (this._struct.zIndex !== value) {
            this._struct.zIndex = value;
            this.parentRepaint();
        }
    }
    get stackingRoot() {
        return this._struct.stackingRoot;
    }
    set stackingRoot(value) {
        this._struct.stackingRoot = value;
    }
    updateZOrder() {
        let array = this._$children;
        if (!array || array.length < 2)
            return;
        let i = 1, j, len = array.length, key, c, d;
        let structArray = this._struct.children;
        while (i < len) {
            j = i;
            c = array[j];
            d = structArray[j];
            key = array[j]._zOrder;
            while (--j > -1) {
                if (array[j]._zOrder > key) {
                    array[j + 1] = array[j];
                    structArray[j + 1] = structArray[j];
                }
                else
                    break;
            }
            array[j + 1] = c;
            structArray[j + 1] = d;
            i++;
        }
        this._struct.children = structArray;
        let flag = this._setBit(NodeFlags.HAS_ZORDER, false);
        this._childChanged();
        if (flag)
            this._setBit(NodeFlags.HAS_ZORDER, true);
    }
    get texture() {
        return this._texture;
    }
    set texture(value) {
        if (this._texture == value)
            return;
        this._texture && this._texture._removeReference();
        this._texture = value;
        if (value) {
            value._addReference();
            this.graphics.repaint();
        }
        else {
            if (this._graphics) {
                this._graphics.repaint();
            }
            else {
                this.repaint();
            }
        }
    }
    get material() {
        var _a;
        return (_a = this._graphics) === null || _a === void 0 ? void 0 : _a.material;
    }
    set material(value) {
        if (value && !this._isMaterialVaild(value))
            return;
        if (this._graphics == null && value == null)
            return;
        this.graphics.material = value;
    }
    get renderNode2D() {
        return this._renderNode;
    }
    set renderNode2D(value) {
        this._renderNode = value;
        if (value) {
            value.renderUpdate && this._struct.setRenderUpdateCallback(value.renderUpdate.bind(value));
            this._renderType |= SpriteConst.RENDERNODE2D;
        }
        else {
            this._struct.setRenderUpdateCallback(null);
            this._renderType &= ~SpriteConst.RENDERNODE2D;
        }
        if (this._graphics) {
            this._graphics.repaint();
        }
    }
    get autoSize() {
        return this._autosize;
    }
    set autoSize(value) {
        this._autosize = value;
    }
    pos(x, y) {
        if (this._x != x || this._y != y) {
            this._x = x;
            this._y = y;
            this._transChanged(TransformKind.Pos);
        }
        return this;
    }
    pivot(x, y) {
        if (this._pivotX != x || this._pivotY != y) {
            this._pivotX = x;
            this._pivotY = y;
            let t = this.width;
            if (t != 0)
                this._anchorX = x / t;
            t = this.height;
            if (t != 0)
                this._anchorY = y / t;
            this._transChanged(TransformKind.Anchor);
        }
        return this;
    }
    anchor(x, y) {
        if (this._anchorX != x || this._anchorY != y) {
            this._anchorX = x;
            this._anchorY = y;
            this._pivotX = x * this.width;
            this._pivotY = y * this.height;
            this._transChanged(TransformKind.Anchor);
        }
        return this;
    }
    size(width, height) {
        let bw, bh;
        if (width == null) {
            bw = (this._sizeFlag & 1) != 0;
            this._sizeFlag &= ~1;
        }
        else {
            bw = this._width != width || (this._sizeFlag & 1) == 0;
            this._width = width;
            this._pivotX = this._anchorX * width;
            this._sizeFlag |= 1;
        }
        if (height == null) {
            bh = (this._sizeFlag & 2) != 0;
            this._sizeFlag &= ~2;
        }
        else {
            bh = this._height != height || (this._sizeFlag & 2) == 0;
            this._height = height;
            this._pivotY = this._anchorY * height;
            this._sizeFlag |= 2;
        }
        if (bw || bh)
            this._transChanged((bw ? TransformKind.Width : 0) | (bh ? TransformKind.Height : 0));
        return this;
    }
    scale(x, y) {
        if (this._scaleX !== x || this._scaleY !== y) {
            this._scaleX = x;
            this._scaleY = y;
            this._transChanged(TransformKind.Scale);
        }
        return this;
    }
    skew(x, y) {
        if (this._skewX !== x || this._skewY !== y) {
            this._skewX = x;
            this._skewY = y;
            this._transChanged(TransformKind.Skew);
        }
        return this;
    }
    _transChanged(kind) {
        var _a;
        if (this._destroyed)
            return;
        if (this._oriRenderPass)
            this._oriRenderPass.repaint = true;
        const parentPassRepaint = this._struct.inheritedEnableCulling || this._struct.inheritedDcOptimize;
        if (kind !== TransformKind.Pos && kind !== TransformKind.Anchor) {
            this._tfChanged = true;
            if ((kind & TransformKind.Size) !== 0 && this._graphics)
                this._graphics.repaint();
            else if ((this._renderType & SpriteConst.DRAW2RT) !== 0)
                this.repaint();
            else {
                this.parentRepaint(parentPassRepaint);
            }
        }
        else {
            this.parentRepaint(parentPassRepaint);
        }
        (_a = this._maskParent) === null || _a === void 0 ? void 0 : _a.repaint(RepaintFlag.ChildChange);
        if ((kind & TransformKind.TRS) !== 0
            || (kind & TransformKind.Anchor) !== 0
            || ((kind & TransformKind.Size) !== 0 && (this._anchorX !== 0 || this._anchorY !== 0 || this._struct.inheritedDcOptimize))) {
            this._globalTrans._spTransChanged(kind);
            if (this._getBit(NodeFlags.DEMAND_TRANS_EVENT))
                notifyTransChanged(this);
        }
    }
    drawToCanvas(canvasWidth, canvasHeight, offsetX, offsetY) {
        return Sprite.drawToCanvas(this, canvasWidth, canvasHeight, offsetX, offsetY);
    }
    static drawToCanvas(sprite, canvasWidth, canvasHeight, offsetX, offsetY) {
        let rt = Sprite.drawToRenderTexture2D(sprite, canvasWidth, canvasHeight, offsetX, offsetY, null);
        var dt = rt.getData(0, 0, canvasWidth, canvasHeight);
        var imgdata = new ImageData(canvasWidth, canvasHeight);
        ;
        var lineLen = canvasWidth * 4;
        var dst = imgdata.data;
        var y = canvasHeight - 1;
        var off = y * lineLen;
        var srcoff = 0;
        for (; y >= 0; y--) {
            dst.set(dt.subarray(srcoff, srcoff + lineLen), off);
            off -= lineLen;
            srcoff += lineLen;
        }
        let canv = new HTMLCanvas();
        canv.size(canvasWidth, canvasHeight);
        canv.context.putImageData(imgdata, 0, 0);
        rt.destroy();
        return canv;
    }
    drawToTexture(canvasWidth, canvasHeight, offsetX, offsetY, rt = null, isDrawRenderRect = true) {
        let res = Sprite.drawToTexture(this, canvasWidth, canvasHeight, offsetX, offsetY, rt, isDrawRenderRect);
        return res;
    }
    static drawToTexture(sprite, canvasWidth, canvasHeight, offsetX, offsetY, rt = null, isDrawRenderRect = true) {
        return Sprite.drawToRenderTexture2D(sprite, canvasWidth, canvasHeight, offsetX, offsetY, rt, isDrawRenderRect);
    }
    drawToRenderTexture2D(canvasWidth, canvasHeight, offsetX, offsetY, rt, isDrawRenderRect, flipY, clearColor, renderScaleX = 1, renderScaleY = 1) {
        let res = Sprite.drawToRenderTexture2D(this, canvasWidth, canvasHeight, offsetX, offsetY, rt, isDrawRenderRect, flipY, clearColor, renderScaleX, renderScaleY);
        return res;
    }
    static drawToRenderTexture2D(sprite, canvasWidth, canvasHeight, offsetX, offsetY, rt, isDrawRenderRect, flipY, clearColor, renderScaleX = 1, renderScaleY = 1) {
        var _a;
        let renderout = rt || new RenderTexture2D(canvasWidth, canvasHeight, RenderTargetFormat.R8G8B8A8);
        renderout._invertY = flipY;
        if (LayaGL.renderEngine._screenInvertY) {
            renderout._invertY = !renderout._invertY;
        }
        let processor = new Render2DProcessor();
        let pass = processor.basePass;
        if (clearColor) {
            pass.setClearColor(clearColor.r, clearColor.g, clearColor.b, clearColor.a);
            pass.doClearColor = true;
        }
        let { root, subPasses } = sprite.prepareOSR();
        for (let subPass of subPasses) {
            processor.addPass(subPass);
        }
        pass.renderTexture = renderout;
        pass.root = root;
        let matrix = pass.offsetMatrix;
        matrix.identity();
        let local = sprite.transform;
        if (local)
            local.copyTo(matrix);
        matrix.scale(renderScaleX, renderScaleY);
        matrix.invert();
        matrix.tx = -offsetX;
        matrix.ty = -offsetY;
        pass.offsetMatrix = matrix;
        let globalSD = (_a = sprite._struct.globalRenderData) === null || _a === void 0 ? void 0 : _a.globalShaderData;
        let hadCamera2D = false;
        if (globalSD && globalSD.hasDefine(ShaderDefines2D.SHADERDEFINE_CAMERA2D)) {
            hadCamera2D = true;
            globalSD.removeDefine(ShaderDefines2D.SHADERDEFINE_CAMERA2D);
        }
        processor.apply(Render2DProcessor.rendercontext2D);
        if (hadCamera2D) {
            globalSD.addDefine(ShaderDefines2D.SHADERDEFINE_CAMERA2D);
        }
        processor.clear();
        pass.destroy();
        return renderout;
    }
    prepareOSR() {
        let runner = Render2DProcessor.runner;
        let passSet = new Set();
        const updateSprites = function (sprite) {
            if (!sprite._struct || !sprite._struct.enabled)
                return;
            if (sprite._subpassUpdateFlag) {
                if (sprite._manualRender) {
                    sprite._subpassUpdateFlag = 0;
                }
                else {
                    sprite.updateSubRenderPassState();
                    if (sprite._oriRenderPass) {
                        let result = sprite.updateRenderTexture();
                        let destrt = sprite._drawOriRT;
                        if (destrt) {
                            sprite._oriRenderPass.renderTexture = destrt;
                            if (sprite.mask) {
                                sprite._oriRenderPass.mask = sprite.mask._struct;
                            }
                            else
                                sprite._oriRenderPass.mask = null;
                            if (result) {
                                sprite._oriRenderPass.renderTexture = destrt;
                            }
                            let process = sprite._renderType & SpriteConst.POSTPROCESS ? sprite.postProcess : null;
                            if (process
                                && destrt != RenderTexture2D._empty) {
                                if (result ||
                                    (sprite._subpassUpdateFlag & SubPassFlag.UPDATE_POSTPROCESS)) {
                                    process.setResource(destrt);
                                    process.clearCMD();
                                    process._render();
                                }
                                if (process.enabled) {
                                    destrt = process._context.destination;
                                }
                            }
                            sprite._subStructRender._updateRenderTexture(sprite._drawOriRT, destrt);
                            sprite._subpassUpdateFlag = 0;
                        }
                        else {
                            sprite.setSubRenderPassState(false);
                        }
                    }
                }
            }
            if (sprite._struct) {
                sprite._updateStruct();
                if (sprite._struct.pass)
                    passSet.add(sprite._struct.pass);
            }
            if (sprite._graphics) {
                sprite._graphicsRenderer._render(runner, 0, 0);
            }
            for (let i = 0, len = sprite._children.length; i < len; i++)
                updateSprites(sprite._children[i]);
        };
        updateSprites(this);
        let root = this._oriRenderPass && this._oriRenderPass.enable ? this._subStruct : this._struct;
        let subPasses = Array.from(passSet).filter(pass => pass.priority > 0);
        return { root, subPasses };
    }
    hitTestPoint(x, y) {
        let point = this.globalToLocal(tmpPoint.setTo(x, y));
        x = point.x;
        y = point.y;
        var rect = this._hitArea ? this._hitArea :
            (this._isWidthSet && this._isHeightSet) ? tmpRect.setTo(0, 0, this._width, this._height) : this.getGraphicBounds(false, tmpRect);
        return rect.contains(x, y, this);
    }
    setSelfBounds(bound) {
        this._userBounds = bound;
    }
    getBounds(out) {
        out = this.getSelfBounds(out, true);
        return SpriteUtils.transformRect(this, out, this._parent, out);
    }
    getSelfBounds(out, recursive) {
        var _a;
        if (recursive == null)
            recursive = true;
        if (this._userBounds != null)
            return this._userBounds.clone(out);
        else if (this._scrollRect != null && !this._getBit(NodeFlags.DISABLE_INNER_CLIPPING))
            return this._scrollRect.clone(out);
        else if (!recursive && ((_a = this._oriRenderPass) === null || _a === void 0 ? void 0 : _a.enable))
            return this._subStructRender._rtRect.clone(out);
        else {
            out = this.getGraphicBounds(false, out);
            if (recursive && this._children.length > 0) {
                let rect = Rectangle.create();
                for (let child of this._children) {
                    if (child._struct.enabled && child._maskParent != this) {
                        child.getBounds(rect);
                        out.union(rect, out);
                    }
                }
                rect.recover();
            }
            return out;
        }
    }
    getGraphicBounds(realSize, out) {
        if (out)
            out.setTo(0, 0, 0, 0);
        else
            out = new Rectangle();
        if (this._graphics != null)
            out.union(this._graphics.getBounds(), out);
        if (this._texture != null) {
            let tex = this._texture;
            let width = this._isWidthSet ? this._width : tex.sourceWidth;
            let height = this._isHeightSet ? this._height : tex.sourceHeight;
            if (tex.sourceWidth !== 0 && tex.sourceHeight !== 0) {
                let wRate = width / tex.sourceWidth;
                let hRate = height / tex.sourceHeight;
                out.union(tmpRect.setTo(tex.offsetX * wRate, tex.offsetY * hRate, tex.width * wRate, tex.height * hRate), out);
            }
            else {
                out.union(tmpRect.setTo(0, 0, width, height), out);
            }
        }
        if (this._renderNode != null) {
            let rect = this._renderNode.rect;
            Rectangle.minMaxRect(rect.x, rect.y, rect.z, rect.w, tmpRect);
            out.union(tmpRect, out);
            tmpRect.setTo(0, 0, this._width, this._height);
            out.union(tmpRect, out);
        }
        return out;
    }
    localToGlobal(point, createNewPoint, globalNode) {
        if (createNewPoint) {
            point = new Point(point.x, point.y);
        }
        let ele = this;
        globalNode = globalNode || ILaya.stage;
        while (ele && !ele._destroyed) {
            if (ele == globalNode)
                break;
            point = ele.toParentPoint(point);
            ele = ele._parent;
        }
        return point;
    }
    globalToLocal(point, createNewPoint, globalNode) {
        if (createNewPoint) {
            point = new Point(point.x, point.y);
        }
        let ele = this;
        tmpSpriteList.length = 0;
        globalNode = globalNode || ILaya.stage;
        while (ele && !ele._destroyed) {
            if (ele == globalNode)
                break;
            tmpSpriteList.push(ele);
            ele = ele._parent;
        }
        let i = tmpSpriteList.length - 1;
        while (i >= 0) {
            ele = tmpSpriteList[i];
            point = ele.fromParentPoint(point);
            i--;
        }
        return point;
    }
    toParentPoint(point) {
        var _a;
        if (!point)
            return point;
        point.x -= this.pivotX;
        point.y -= this.pivotY;
        (_a = this.transform) === null || _a === void 0 ? void 0 : _a.transformPoint(point);
        point.x += this._x;
        point.y += this._y;
        let scroll = this._scrollRect;
        if (scroll) {
            point.x -= scroll.x * this._scaleX;
            point.y -= scroll.y * this._scaleY;
        }
        return point;
    }
    fromParentPoint(point) {
        var _a;
        if (!point)
            return point;
        point.x -= this._x;
        point.y -= this._y;
        var scroll = this._scrollRect;
        if (scroll) {
            point.x += scroll.x * this._scaleX;
            point.y += scroll.y * this._scaleY;
        }
        (_a = this.transform) === null || _a === void 0 ? void 0 : _a.invertTransformPoint(point);
        point.x += this.pivotX;
        point.y += this.pivotY;
        return point;
    }
    loadImage(url, complete) {
        if (!url) {
            this.texture = null;
            complete && complete.run();
        }
        else {
            let tex = ILaya.loader.getRes(url);
            if (tex) {
                this.texture = tex;
                complete && complete.run();
            }
            else {
                if (this._skinBaseUrl)
                    url = URL.formatURL(url, this._skinBaseUrl);
                ILaya.loader.load(url).then((tex) => {
                    this.texture = tex;
                    complete && complete.run();
                });
            }
        }
        return this;
    }
    static fromImage(url) {
        return new Sprite().loadImage(url);
    }
    reCache() {
        if (this._cacheAsBmp || this._mask)
            this.repaint();
    }
    getRepaint() {
        return this._repaint;
    }
    repaint(flag) {
        if (this._destroyed)
            return;
        if (this._repaint < Stat.loopCount
            || (this._repaint === Stat.loopCount && this._repaintCount < Stat.render2DCount)
            || !this._previousType) {
            this._repaint = Stat.loopCount;
            this._repaintCount = Stat.render2DCount;
            this._previousType = this._renderType;
            this._struct.setRepaint();
            this.stage._graphicUpdateList.add(this);
            this.parentRepaint();
            if (this._renderType & SpriteConst.DRAW2RT) {
                if (!this._drawOriRT
                    || this._subpassUpdateFlag
                    || flag & RepaintFlag.UpdateRT
                    || (this.transform && this._maskParent)) {
                    this.setSubpassFlag(SubPassFlag.RenderTexture);
                }
            }
            if (this._renderType & SpriteConst.GRAPHICS) {
                if (flag & RepaintFlag.Graphics) {
                    if (this._graphicsRenderer)
                        this._graphicsRenderer.onModified();
                }
                this._globalTrans._notifyRenderSpriteTransChange();
            }
        }
        if (this._maskParent) {
            this._maskParent.setSubpassFlag(SubPassFlag.Mask);
            this._maskParent.repaint(flag);
        }
    }
    _needGraphicsUpdate() {
        return !this._destroyed
            && this._struct.enabled
            && this._renderType & SpriteConst.GRAPHICS
            && !!(this.displayedInStage || this._maskParent);
    }
    clearRepaint() {
        this._repaint = -1;
        this._repaintCount = -1;
    }
    _needRepaint() {
        return !!(this._repaint >= Stat.loopCount && this._repaintCount >= LayaGL.renderEngine._framePassCount && this._previousType === 0);
    }
    parentRepaint(repaintPass = true) {
        let p = this._parent;
        if (!p)
            return;
        let pStruct = p._struct;
        let pass = pStruct ? pStruct.pass : null;
        if (pStruct && pass) {
            if (pass.renderTexture) {
                p.parentRepaint();
                if (p._renderType & SpriteConst.DRAW2RT && !p._needRepaint()) {
                    p.setSubpassFlag(SubPassFlag.RenderTexture);
                    pStruct.setRepaint();
                }
            }
            else if (repaintPass)
                pStruct.setRepaint();
        }
    }
    get dragSupport() {
        return this._dragSupport || (this._dragSupport = new DragSupport(this));
    }
    startDrag(area, hasInertia, elasticDistance, elasticBackTime, data, ratio) {
        let d = this.dragSupport;
        if (area != null)
            d.area.copyFrom(area);
        else
            d.area.reset();
        d.hasInertia = !!hasInertia;
        elasticDistance != null && (d.elasticDistance = elasticDistance);
        elasticBackTime != null && (d.elasticBackTime = elasticBackTime);
        ratio != null && (d.ratio = ratio);
        d.start(data);
    }
    stopDrag() {
        this._dragSupport && this._dragSupport.stop();
    }
    onAfterDeserialize() {
        super.onAfterDeserialize();
        if (LayaEnv.isPlaying) {
            if (this._gcmds) {
                this.graphics.cmds = this._gcmds;
                delete this._gcmds;
            }
            if (this._filters) {
                this.filters = this._filters;
                delete this._filters;
            }
        }
        if (this._mouseState === 2)
            this.setMouseEnabledUp();
    }
    onStartListeningToType(type) {
        super.onStartListeningToType(type);
        if (Event.isMouseEvent(type)) {
            if (this._mouseState === 0) {
                this._setBit(NodeFlags.CHECK_INPUT, true);
                this.setMouseEnabledUp();
            }
        }
        else if (type === Event.TRANSFORM_CHANGED) {
            this._setBit(NodeFlags.DEMAND_TRANS_EVENT, true);
            this.setDemandTransEventUp();
        }
    }
    setDemandTransEventUp() {
        let p = this._parent;
        while (p && p !== ILaya.stage) {
            if (!p._setBit(NodeFlags.DEMAND_TRANS_EVENT, true))
                break;
            p = p._parent;
        }
    }
    _processVisible(parentVisible) {
        if (parentVisible == null)
            parentVisible = !(this._parent instanceof Sprite) || this._parent._struct.enabled;
        let b = parentVisible && this._visible && !this._getBit(hiddenBits);
        let changed = false;
        if (this._struct && this._struct.enabled !== b) {
            changed = true;
            this._struct.enabled = b;
            if (b) {
                this.repaint();
            }
            else {
                this._struct.setRepaint();
            }
            this.parentRepaint();
            this._checkSubRenderPass();
            this._refreshRenderPass();
            this._processChildrenVisible(b);
        }
        return changed;
    }
    _processChildrenVisible(parentVisible) {
        for (let child of this._children) {
            if (child instanceof Sprite)
                child._processVisible(parentVisible);
        }
    }
    _setUnBelongScene() {
        if (this._ownerArea != null) {
            this._ownerArea = null;
        }
        super._setUnBelongScene();
    }
    _setBelongScene(scene) {
        super._setBelongScene(scene);
        this._findOwnerArea();
    }
    _findOwnerArea() {
        let ele = this;
        while (ele) {
            if (ele === this._scene || ele === ILaya.stage)
                break;
            if (ele._globalRenderData) {
                this._ownerArea = ele;
                break;
            }
            ele = ele._parent;
        }
    }
    _setStructParent(value, index) {
        let struct = this._struct;
        if (struct && struct.parent) {
            struct.parent.removeChild(struct);
            struct.parent = null;
        }
        if (value && value._struct) {
            value._struct.addChild(struct, index);
        }
    }
    _ensureSubPassBase() {
        if (this._oriRenderPass)
            return;
        let subPass = LayaGL.render2DRenderPassFactory.createRender2DPass();
        subPass.root = this._struct;
        subPass.enable = false;
        subPass.setClearColor(0, 0, 0, 0);
        let subStruct = LayaGL.render2DRenderPassFactory.createRenderStruct2D();
        subStruct.owner = this;
        subStruct.pass = subPass;
        this._subStruct = subStruct;
        this._oriRenderPass = subPass;
        subStruct.renderMatrix = this.globalTrans.getMatrix();
    }
    _ensureSubStructRender() {
        if (this._subStructRender)
            return;
        this._subStructRender = new SubStructRender();
        this._subStructRender.bind(this, this._oriRenderPass, this._subStruct);
    }
    createSubRenderPass() {
        this._ensureSubPassBase();
        this._ensureSubStructRender();
    }
    updateRenderTexture() {
        let rect = Rectangle.create();
        let oriRect = Rectangle.create();
        if (this._mask) {
            SpriteUtils.getRect(this._mask, false, rect);
            if (this._mask.transform) {
                rect.transform(this._mask.transform, rect);
            }
            rect.x += this._mask._pivotX;
            rect.y += this._mask._pivotY;
        }
        else {
            SpriteUtils.getRect(this, false, rect);
            if (this._maskParent && this.transform) {
                rect.transform(this.transform, rect);
            }
            rect.x += this._pivotX;
            rect.y += this._pivotY;
        }
        let scaleX = 1, scaleY = 1;
        let oldRT = this._drawOriRT;
        let maskRect = this._subStructRender._rtRect;
        rect.width = MathUtil.roundTo(rect.width);
        rect.height = MathUtil.roundTo(rect.height);
        rect.cloneTo(oriRect);
        if (Config.useRetinalCanvas) {
            scaleX = ILaya.stage._scaleX;
            scaleY = ILaya.stage._scaleY;
            rect.width = Math.round(rect.width * scaleX);
            rect.height = Math.round(rect.height * scaleY);
            rect.x = rect.x * scaleX;
            rect.y = rect.y * scaleY;
        }
        if (oldRT) {
            if (maskRect.width === rect.width && maskRect.height === rect.height) {
                this._subStructRender._updateRenderOffset(rect, oriRect, scaleX, scaleY);
                rect.recover();
                oriRect.recover();
                return false;
            }
            if (oldRT !== RenderTexture2D._empty)
                RenderTexture2D.recoverToPool(oldRT);
        }
        this._subStructRender._updateRenderOffset(rect, oriRect, scaleX, scaleY);
        if (rect.width === 0 || rect.height === 0) {
            this._drawOriRT = RenderTexture2D._empty;
        }
        else {
            let multiSamples = LayaGL.renderEngine.getCapable(RenderCapable.MSAA) ? 4 : 1;
            let renderTexture = RenderTexture2D.createFromPool(rect.width, rect.height, RenderTargetFormat.R8G8B8A8, RenderTargetFormat.DEPTHSTENCIL_24_8, multiSamples);
            renderTexture._invertY = LayaGL.renderEngine._screenInvertY;
            this._drawOriRT = renderTexture;
        }
        rect.recover();
        oriRect.recover();
        return true;
    }
    updateSubRenderPassState() {
        this.setSubRenderPassState((this._renderType & SpriteConst.DRAW2RT) !== 0);
    }
    _updateStruct() {
        let trans = this.globalTrans;
        if (this._destroyed || !trans)
            return;
        let matrix = trans.getMatrix();
        let struct = this._struct;
        this._struct.renderMatrix = matrix;
        if (this._subStruct)
            this._subStruct.renderMatrix = matrix;
        let rect = struct.rect;
        if (this._struct.inheritedEnableCulling || this._struct.inheritedDcOptimize) {
            this.getSelfBounds(rect, false);
            rect.transform(matrix, rect);
            struct.rect = rect;
        }
        else {
            struct.rect.reset();
        }
        if (this._subStruct) {
            rect.cloneTo(this._subStruct.rect);
            this._subStruct.rect = this._subStruct.rect;
        }
    }
    setSubRenderPassState(enable) {
        if (!this._oriRenderPass) {
            if (!enable)
                return;
            this.createSubRenderPass();
        }
        if (enable && !this._oriRenderPass.enable) {
            this._struct.pass = this._oriRenderPass;
            this._struct.subStruct = this._subStruct;
            this._subStruct.enabled = true;
            if (this._maskParent) {
                this._subStruct.blendMode = BlendMode.mask;
            }
        }
        else if (!enable && this._oriRenderPass && this._oriRenderPass.enable) {
            this._struct.pass = null;
            this._subStruct.enabled = false;
            this._subStruct.blendMode = this._blendMode;
            this._struct.subStruct = null;
            this._struct.setRepaint();
        }
        this._oriRenderPass.enable = enable;
        this._refreshRenderPass();
    }
    _setParent(value, index = -1) {
        this._globalTrans._spTransChanged(TransformKind.TRS);
        super._setParent(value, index);
        this._setStructParent(value, index);
        this._processVisible();
        if (value && (this._mouseState === 2 || this._mouseState === 0 && this._getBit(NodeFlags.CHECK_INPUT))
            && !value._getBit(NodeFlags.CHECK_INPUT)) {
            this.setMouseEnabledUp();
        }
        if (value && this._getBit(NodeFlags.DEMAND_TRANS_EVENT) && !value._getBit(NodeFlags.DEMAND_TRANS_EVENT))
            this.setDemandTransEventUp();
    }
    _checkSubRenderPass() {
        if (this._needUpdateSubpass()) {
            if (this._subpassUpdateFlag || (this._renderType & SpriteConst.DRAW2RT && !this._drawOriRT)) {
                this.setSubpassFlag(SubPassFlag.RenderTexture);
            }
        }
        else if (this._subpassUpdateFlag) {
            ILaya.stage._subpassUpdateList.delete(this);
        }
        if (this._mask) {
            this._mask._checkSubRenderPass();
        }
    }
    _refreshRenderPass() {
        if (this._oriRenderPass) {
            let result = this._needUpdateSubpass() && this._oriRenderPass.enable && this._renderType & SpriteConst.DRAW2RT;
            if (result) {
                ILaya.stage.passManager.addPass(this._oriRenderPass);
            }
            else {
                if (this._drawOriRT && this._drawOriRT !== RenderTexture2D._empty) {
                    RenderTexture2D.recoverToPool(this._drawOriRT);
                }
                this._drawOriRT = null;
                if (this._oriRenderPass.postProcess) {
                    this._oriRenderPass.postProcess.recoverAllRTS();
                }
                this._oriRenderPass.repaint = true;
                ILaya.stage.passManager.removePass(this._oriRenderPass);
            }
        }
        if (this._mask) {
            this._mask._refreshRenderPass();
            if (!this._mask.displayedInStage) {
                this._mask.repaint(RepaintFlag.Graphics);
            }
        }
    }
    _setDisplay(value) {
        super._setDisplay(value);
        if (this._needGraphicsUpdate()) {
            if (this._graphicsRenderer)
                this._graphicsRenderer.onModified();
            this.stage._graphicUpdateList.add(this);
            this._globalTrans._notifyRenderSpriteTransChange();
        }
        this._checkSubRenderPass();
        this._refreshRenderPass();
    }
    _setChildIndex(node, oldIndex, index) {
        let out = super._setChildIndex(node, oldIndex, index);
        if (node._struct)
            node._parent._struct.updateChildIndex(node._struct, oldIndex, out);
        return out;
    }
    _childChanged(child) {
        super._childChanged(child);
        if (child) {
            if (child._zOrder)
                this._setBit(NodeFlags.HAS_ZORDER, true);
        }
        if (this._getBit(NodeFlags.HAS_ZORDER))
            ILaya.systemTimer.callLater(this, this.updateZOrder);
        this.repaint(RepaintFlag.ChildChange);
    }
    _addComponentInstance(comp) {
        var _a;
        if (comp instanceof BaseRenderNode2D &&
            ((_a = this._components) === null || _a === void 0 ? void 0 : _a.some((c) => c instanceof BaseRenderNode2D))) {
            console.warn(`${this.name} add RenderNode2D invalid, one sprite can only add one RenderNode`);
            return;
        }
        super._addComponentInstance(comp);
    }
}
const tmpRect = new Rectangle();
const tmpPoint = new Point();
const tmpSpriteList = [];
function notifyTransChanged(sp) {
    sp.event(Event.TRANSFORM_CHANGED);
    for (let child of sp._children) {
        if (child._getBit(NodeFlags.DEMAND_TRANS_EVENT))
            notifyTransChanged(child);
    }
}
